import React, { FC, ReactElement, useState, useEffect } from 'react'
import * as yup from 'yup'
import { Formik } from 'formik'
import { Form, Alert } from 'react-bootstrap'
import API from '@aws-amplify/api'

import LoadingButton from '../../LoadingButton'
import { getEnvironment } from '../../office'

interface Props {
  setIsSubmitted: (isSubmitted: boolean) => void
}

const schema = yup.object({
  name: yup.string().required('Name is a required field'),
  email: yup
    .string()
    .email('Email must be a valid email')
    .required('Email is a required field'),
  company: yup.string().required('Company is a required field'),
  client: yup
    .string()
    .oneOf(['google', 'ms'], 'Must be one of the platform choices')
    .required('Platform choice is a required field'),
})

const RegisterForm: FC<Props> = ({ setIsSubmitted }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isOffice, setIsOffice] = useState(true)

  useEffect(() => {
    const environment = getEnvironment()

    // TODO: we can make this smarter, where we can determine if the user is
    // registering for office desktop, office web, chrome, etc.
    if (environment === 'chrome') {
      setIsOffice(false)
    }
  }, [])

  const register = async (values: {
    name: string
    email: string
    company: string
    client: string
  }): Promise<void> => {
    setIsLoading(true)

    const newUser = {
      name: values.name,
      email: values.email,
      company: values.company,
      client: values.client,
    }

    try {
      await API.post('mav-outlook-api', '/contact/inquire', {
        body: {
          newUser,
        },
      })

      setErrorMessage('')
      setIsSubmitted(true)
    } catch (e) {
      setIsSubmitted(false)
      setErrorMessage(e.message)
      setIsLoading(false)
    }
  }

  return (
    <>
      {errorMessage && (
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <hr />
          <span>{errorMessage}</span>
        </Alert>
      )}
      <Formik
        validationSchema={schema}
        onSubmit={register}
        initialValues={{
          name: '',
          email: '',
          company: '',
          client: isOffice ? 'ms' : 'google',
        }}
        enableReinitialize
      >
        {({ handleSubmit, values, handleChange, touched, errors, submitCount }): ReactElement => (
          <Form noValidate onSubmit={handleSubmit}>
            <div className="controls">
              <Form.Group controlId="name">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={values.name}
                  onChange={handleChange}
                  isValid={touched.name && !errors.name}
                  isInvalid={submitCount > 0 && !!errors.name}
                  placeholder="ex. James Crewston"
                />
                <Form.Control.Feedback type="invalid">
                  <Alert variant="danger">{errors.name}</Alert>
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="company">
                <Form.Label>Company</Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={values.company}
                  onChange={handleChange}
                  isValid={touched.company && !errors.company}
                  isInvalid={submitCount > 0 && !!errors.company}
                  placeholder="ex. Flightwear Ltd."
                />
                <Form.Control.Feedback type="invalid">
                  <Alert variant="danger">{errors.company}</Alert>
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="controls">
              <Form.Group controlId="email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  required
                  value={values.email}
                  onChange={handleChange}
                  isValid={touched.email && !errors.email}
                  isInvalid={submitCount > 0 && !!errors.email}
                  placeholder="ex. james@flightwear.com"
                />
                <Form.Control.Feedback type="invalid">
                  <Alert variant="danger">{errors.email}</Alert>
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group controlId="client">
                <Form.Label>How will you be using MaverickApp?</Form.Label>
                <Form.Control
                  as="select"
                  required
                  value={values.client}
                  onChange={handleChange}
                  isValid={touched.client && !errors.client}
                  isInvalid={submitCount > 0 && !!errors.client}
                  placeholder="Choose one"
                >
                  <option value="google">Chrome Extension</option>
                  <option value="ms">Outlook</option>
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                  <Alert variant="danger">{errors.client}</Alert>
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <Form.Group controlId="actions" className="actions">
              <LoadingButton
                className="register-button"
                variant="primary"
                type="submit"
                block
                isLoading={isLoading}
              >
                Contact me about my hangar
              </LoadingButton>
            </Form.Group>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default RegisterForm
